/**
 * Client for the deposit-release endpoint.
 *
 * Two modes, one endpoint. The representative arrives with a token and no
 * session — a lessor is a record with an email, not necessarily a user account —
 * so that path sends the anon key and nothing else. An admin recording a decision
 * by phone sends their own session token and a settlement id instead.
 *
 * Every response carries the fresh server-computed state, including the refund
 * total, so the page never derives money locally. The database recomputes the
 * refund from the deduction rows; anything this file calculated would be a second
 * opinion nobody asked for.
 */

import { supabase, supabaseAnonKey, supabaseUrl } from "./supabase";

export type DeductionCategory =
  | "damage"
  | "cleaning"
  | "unpaid_rent"
  | "fees"
  | "other";

export type ReleaseDecision = "full" | "partial" | "none";

export interface ReleaseDeductionRow {
  id: string;
  category: DeductionCategory;
  amount_cents: number;
  reason: string;
  evidence_paths: string[];
  created_by_name: string | null;
  created_at: string;
  sort_order: number;
}

export interface ReleaseReviewState {
  settlement_id: string;
  status: string;
  tenant_name: string | null;
  room_number: string | null;
  property_name: string | null;
  address: string | null;
  tenancy_start: string | null;
  move_out_date: string | null;
  deposit_held_cents: number;
  deposit_expected_cents: number;
  refund_cents: number;
  excess_cents: number;
  deductions: ReleaseDeductionRow[];
  bundle_url: string | null;
  bundle_page_count: number | null;
  released_at: string | null;
  released_by_name: string | null;
  decision: ReleaseDecision | null;
  suggested_unpaid_rent: { amount_cents: number; reason: string } | null;
}

/** How the caller proves they may act on this settlement. */
export type ReleaseAuth =
  | { mode: "token"; token: string }
  | { mode: "admin"; settlementId: string };

/**
 * A fact about what a release-flow action just did, for the caller to log to
 * the tenant's activity timeline.
 *
 * Emitted by `DepositReleasePanel` and `DepositRefundForm`, both of which stay
 * ignorant of `audit_logs` — writing there needs an admin session, and the
 * representative's token-authed page has neither one nor any business logging
 * under an admin's name. `TenantProfileDialog` is the one place with that
 * session, so it is the one place that turns this into a `logAudit` call; the
 * representative's page simply never wires the callback.
 */
export type DepositReleaseLogEvent =
  | {
      kind: "deduction_added" | "deduction_updated" | "deduction_deleted";
      category: DeductionCategory;
      amountCents: number;
    }
  | {
      kind: "released";
      decision: ReleaseDecision;
      refundCents: number;
      excessCents: number;
    }
  | {
      kind: "released_by_amount";
      refundCents: number;
      excessCents: number;
      /** Whether a transfer proof closed the settlement in the same call. */
      paid: boolean;
    };

interface Envelope {
  ok?: boolean;
  error?: string;
  state?: ReleaseReviewState;
  refund_cents?: number;
  excess_cents?: number;
  /** release_by_amount: whether the transfer proof also closed the settlement. */
  refund_paid?: boolean;
  statement_warning?: string | null;
  owner_warning?: string | null;
}

async function call(
  auth: ReleaseAuth,
  payload: Record<string, unknown>
): Promise<Envelope & ReleaseReviewState> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: supabaseAnonKey,
  };
  const body: Record<string, unknown> = { ...payload };

  if (auth.mode === "token") {
    // No session exists on this path, so the anon key is the only credential —
    // authorisation is the token, checked in-function against a stored hash.
    headers.Authorization = `Bearer ${supabaseAnonKey}`;
    body.token = auth.token;
  } else {
    const { data } = await supabase.auth.getSession();
    const accessToken = data?.session?.access_token;
    if (!accessToken)
      throw new Error("Your session has expired. Sign in again.");
    headers.Authorization = `Bearer ${accessToken}`;
    body.settlement_id = auth.settlementId;
  }

  const res = await fetch(`${supabaseUrl}/functions/v1/deposit-release`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  let parsed: (Envelope & Partial<ReleaseReviewState>) | null = null;
  try {
    parsed = await res.json();
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    throw new Error(parsed?.error || `Request failed (${res.status})`);
  }
  return (parsed ?? {}) as Envelope & ReleaseReviewState;
}

/** Load the review state. `get` also stamps the first open on the token path. */
export async function fetchReleaseState(
  auth: ReleaseAuth
): Promise<ReleaseReviewState> {
  return (await call(auth, { action: "get" })) as ReleaseReviewState;
}

const stateOf = (
  envelope: Envelope & ReleaseReviewState
): ReleaseReviewState => {
  // Mutations wrap the state; `get` returns it bare.
  if (envelope.state) return envelope.state;
  return envelope as ReleaseReviewState;
};

export async function addDeduction(
  auth: ReleaseAuth,
  input: { category: DeductionCategory; amountCents: number; reason: string }
): Promise<ReleaseReviewState> {
  return stateOf(
    await call(auth, {
      action: "add_deduction",
      category: input.category,
      amount_cents: input.amountCents,
      reason: input.reason,
    })
  );
}

export async function updateDeduction(
  auth: ReleaseAuth,
  deductionId: string,
  input: { category: DeductionCategory; amountCents: number; reason: string }
): Promise<ReleaseReviewState> {
  return stateOf(
    await call(auth, {
      action: "update_deduction",
      deduction_id: deductionId,
      category: input.category,
      amount_cents: input.amountCents,
      reason: input.reason,
    })
  );
}

export async function deleteDeduction(
  auth: ReleaseAuth,
  deductionId: string
): Promise<ReleaseReviewState> {
  return stateOf(
    await call(auth, { action: "delete_deduction", deduction_id: deductionId })
  );
}

export async function releaseDeposit(
  auth: ReleaseAuth,
  input: { decision: ReleaseDecision; note?: string }
): Promise<ReleaseReviewState> {
  return stateOf(
    await call(auth, {
      action: "release",
      decision: input.decision,
      note: input.note ?? null,
    })
  );
}

/**
 * What the server made of a release, plus what it could not finish.
 *
 * The warnings are not failures: by the time they can happen the decision is
 * recorded and, with a proof attached, the money has already moved. They say
 * which mail did not go out, which is a thing an admin should see rather than a
 * thing to hide behind a green toast.
 */
export interface ReleaseByAmountResult extends ReleaseReviewState {
  /** True when a transfer proof was recorded, closing the settlement outright. */
  refundPaid: boolean;
  statementWarning: string | null;
  ownerWarning: string | null;
}

/**
 * Release by typing the refund amount rather than itemising.
 *
 * `reason` is required whenever the refund is less than the deposit held, and it
 * is tenant-visible — the server records the withheld remainder as a real
 * deduction carrying that text, so the statement still explains every euro.
 * `note` is the separate admin-only note and never reaches the tenant.
 *
 * `refundProofPath` is the storage path of the bank confirmation for a transfer
 * that has ALREADY gone out, and it changes what the call means: the settlement
 * closes in one step, the tenancy ends, and the apartment's owner is notified.
 * Admin only — the server refuses it from a representative's token.
 */
export async function releaseDepositByAmount(
  auth: ReleaseAuth,
  input: {
    refundCents: number;
    reason?: string;
    note?: string;
    refundProofPath?: string | null;
  }
): Promise<ReleaseByAmountResult> {
  const envelope = await call(auth, {
    action: "release_by_amount",
    refund_cents: input.refundCents,
    reason: input.reason ?? null,
    note: input.note ?? null,
    refund_proof_path: input.refundProofPath ?? null,
  });
  return {
    ...stateOf(envelope),
    refundPaid: !!envelope.refund_paid,
    statementWarning: envelope.statement_warning ?? null,
    ownerWarning: envelope.owner_warning ?? null,
  };
}

/**
 * Attest that the transfer has gone out. Admin-only — the representative decides
 * the amount, Coliviq moves the money, so only Coliviq can say it moved.
 */
export async function markRefundPaid(
  auth: ReleaseAuth,
  input: { proofPath?: string | null; ibanLast4?: string | null } = {}
): Promise<ReleaseReviewState> {
  return stateOf(
    await call(auth, {
      action: "mark_refund_paid",
      proof_path: input.proofPath ?? null,
      iban_last4: input.ibanLast4 ?? null,
    })
  );
}
